import type { ExternalTableSchema } from '@packages/table-builder';

const disbursementDetailSubRowColumns: ExternalTableSchema['columns'] = [
  {
    field: 'accountNumber',
    header: 'Account Number',
    type: 'text',
    width: 320,
  },
  {
    field: 'brokerage',
    header: 'Brokerage',
    type: 'text',
    width: 200,
  },
  {
    field: 'amount',
    header: 'Amount',
    type: 'currency',
    width: 180,
    align: 'center',
  },
  {
    field: 'status',
    header: 'Status',
    type: 'badge',
    width: 180,
    align: 'center',
    badge: {
      statusMap: {
        Pending: 'info',
        Processing: 'warning',
        Completed: 'success',
        Failed: 'error',
        Cancelled: 'default',
        Disbursed: 'success',
      },
      defaultVariant: 'info',
    },
    editable: { type: 'asyncSelect', defaultValueField: 'status' },
    dataSource: {
      url: '/api/award-settlement/api/app/disbursement/disbursement-item-status',
      mapper: { label: 'name', value: 'name' },
    },
  },
];

export const disbursementDetailTable: ExternalTableSchema = {
  metadata: {
    title: 'Disbursement Details',
    entity: 'DisbursementDetail',
    description: 'Disbursement detail list with editable withdrawal sub-rows.',
  },

  dataSource: {
    endpoint: '/api/award-settlement/api/app/disbursement/disbursement-detail',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Sorting: 'table.sorting',
      SkipCount: 'table.pageOffset',
      MaxResultCount: 'table.pageSize',
      CompanyIds: 'filters.companyIds',
      DisbursementContainerStatusId: 'filters.disbursementContainerStatusIds',
      DisbursementStatusId: 'filters.disbursementStatusIds',
    },
    saveEndpoint: '/api/award-settlement/api/app/disbursement/disbursement',
    saveMethod: 'PUT',
    onSaveSuccess: 'toastAndRefetch',
    saveSuccessMessage: 'Disbursement updated successfully.',
    confirmBeforeSave: {
      title: 'Are you sure you want to save the disbursement changes?',
      message: '',
      variant: 'warning',
    },
    filters: [
      {
        name: 'companyIds',
        semanticId: 'companyIds',
        placeholder: 'Company',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/customer/api/app/participant-broker-account/company-list',
          labelField: 'companyName',
          valueField: 'id',
        },
      },
      {
        name: 'disbursementContainerStatusIds',
        semanticId: 'disbursementContainerStatusIds',
        placeholder: 'Container Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award-settlement/api/app/disbursement/disbursement-container-status',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'disbursementStatusIds',
        semanticId: 'disbursementStatusIds',
        placeholder: 'Disbursement Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award-settlement/api/app/disbursement/disbursement-item-status',
          labelField: 'name',
          valueField: 'id',
        },
      },
    ],
  },

  columns: [
    {
      field: 'company',
      header: 'Company Name',
      type: 'text',
      width: 200,
    },
    {
      field: 'brokerage',
      header: 'Brokerage',
      type: 'text',
      width: 200,
    },
    {
      field: 'ticker',
      header: 'Ticker',
      type: 'text',
      width: 150,
    },
    {
      field: 'completionTime',
      header: 'Completed Date',
      type: 'date',
      width: 150,
      align: 'center',
      editable: { type: 'date' },
    },
    {
      field: 'containerStatus',
      header: 'Container Status',
      type: 'badge',
      width: 180,
      align: 'center',
      badge: {
        statusMap: {
          Pending: 'info',
          'Pending Broker': 'warning',
          Disbursed: 'success',
          Failed: 'error',
        },
        defaultVariant: 'info',
      },
      editable: { type: 'asyncSelect', defaultValueField: 'containerStatus' },
      dataSource: {
        url: '/api/award-settlement/api/app/disbursement/disbursement-container-status',
        mapper: { label: 'name', value: 'name' },
      },
    },
    {
      field: 'disbursementStatus',
      header: 'Disbursement Status',
      type: 'badge',
      width: 200,
      align: 'center',
      badge: {
        statusMap: {
          Pending: 'info',
          Processing: 'warning',
          Disbursed: 'success',
          Failed: 'error',
        },
        defaultVariant: 'info',
      },
      editable: { type: 'asyncSelect', defaultValueField: 'disbursementStatus' },
      dataSource: {
        url: '/api/award-settlement/api/app/disbursement/disbursement-status',
        mapper: { label: 'name', value: 'name' },
      },
    },
  ],

  actions: {
    row: [{ id: 'EDIT_DISBURSEMENT', label: 'Edit', actionType: 'startEditing' }],
  },

  features: {
    search: { enabled: false },
    pagination: { mode: 'infiniteScroll', defaultSize: 10 },
    editing: { bulkSave: true },
    rows: {
      subRows: {
        dataField: 'withdrawal',
        columns: disbursementDetailSubRowColumns,
        expandMode: 'alwaysOpen',
      },
    },
    columnPreferences: { persistenceKey: 'disbursementDetail', resizable: true },
  },
};
